import { isIOS } from '../../support/isIos';
import { computePosition, autoUpdate, offset, flip } from '@floating-ui/dom';

export default (content) => {
    return (inputOptions) => ({
        offset: 0,
        guardOptions: [],
        canFetch: true,
        isBottom: false,

        ...content(inputOptions),

        initPositioningSystem() {
            this.$refs.popover.style.position = 'fixed';
            this.$refs.popover.style.visibility = 'hidden';
            this.$refs.popover.style.width = `${this.$refs.selectBase.offsetWidth}px`;

            this.initInfiniteScroll();

            this.$watch('popover', (state) => {
                this.$watch('search', () => {
                    this.$nextTick(() => {
                        this.canFetch = true;
                        this.offset = 0;
                        this.guardOptions = [];
                        this.asyncData.params['offset'] = 0;
                    });
                });

                if (!state && this.cleanupPosition) {
                    this.cleanupPosition();
                    this.cleanupPosition = null;
                }

                if (state) {
                    this.$refs.popover.style.visibility = 'hidden';
                }

                if (state && !this.cleanupPosition) {
                    this.refreshPopover();
                }
            });

            this.$watch('displayOptions', () => {
                if (!this.guardOptions.length) {
                    this.guardOptions = this.displayOptions;
                }
            });

            this.$watch('search', () => this.refreshPopover());
        },

        resetGuardOptions() {
            this.offset = 0;
            this.guardOptions = [];
            this.asyncData.params['offset'] = 0;
        },

        updatePosition() {
            computePosition(this.$root, this.$refs.popover, {
                placement: 'bottom',
                middleware: [offset(4), flip()],
                strategy: 'fixed',
            }).then(({ x, y }) => {
                isIOS() && this.configureScrollSize();

                this.handleShadow();

                Object.assign(this.$refs.popover.style, {
                    position: 'fixed',
                    left: `${x}px`,
                    top: `${y}px`,
                    width: `${this.$refs.selectBase.offsetWidth}px`,
                });

                setTimeout(() => {
                    this.$refs.popover.style.visibility = 'visible';
                }, 100);
            });
        },

        refreshPopover() {
            this.$nextTick(() => {
                this._waitUntilStable(() => {
                    this.syncPopoverPosition();
                });
            });
        },

        _waitUntilStable(callback) {
            let lastRect = this.$root.getBoundingClientRect();

            const check = () => {
                const rect = this.$root.getBoundingClientRect();

                if (rect.left === lastRect.left && rect.top === lastRect.top) {
                    callback();
                } else {
                    lastRect = rect;
                    requestAnimationFrame(check);
                }
            };

            requestAnimationFrame(check);
        },

        syncPopoverPosition() {
            this.cleanupPosition = autoUpdate(
                this.$root,
                this.$refs.popover,
                () => this.updatePosition(),
                { ancestorResize: true, ancestorScroll: true, elementResize: true },
            );

            if (!this._anchorResizeObserver) {
                this._anchorResizeObserver = new ResizeObserver(() => {
                    if (this.popover) {
                        this.updatePosition();
                    }
                });

                let ancestor = this.$root.parentElement;

                while (ancestor && ancestor !== document.body) {
                    const overflow = window.getComputedStyle(ancestor).overflow;

                    if (overflow === 'auto' || overflow === 'scroll' || overflow === 'hidden') {
                        break;
                    }

                    ancestor = ancestor.parentElement;
                }

                if (ancestor && ancestor !== document.body) {
                    this._anchorResizeObserver.observe(ancestor);
                } else {
                    this._anchorResizeObserver.observe(this.$root.parentElement);
                }

                const origCleanup = this.cleanupPosition;

                this.cleanupPosition = () => {
                    origCleanup();
                    this._anchorResizeObserver?.disconnect();
                    this._anchorResizeObserver = null;
                };
            }
        },

        configureScrollSize() {
            if (isIOS()) {
                const scrollable = document.getElementById(`content-${this.$el.id}`);
                const scroll = document.getElementById(`scroll-${this.$el.id}`);

                scroll.style.display = 'block';

                const thumbVerticalSize =
                    (scrollable.clientHeight / scrollable.scrollHeight) * scrollable.clientHeight;

                if (scrollable.clientHeight < scrollable.scrollHeight) {
                    scroll.style.height = thumbVerticalSize + 'px';
                    scrollable.addEventListener('scroll', () =>
                        this.moveScrollBar(scrollable, scroll),
                    );

                    this.moveScrollBar(scrollable, scroll);
                } else {
                    scroll.style.display = 'none';
                }
            }
        },

        checkIsIos() {
            return isIOS();
        },

        moveScrollBar(scrollable, scroll) {
            const thumbVerticalSize =
                (scrollable.clientHeight / scrollable.scrollHeight) * scrollable.clientHeight;
            const scrollRatio =
                scrollable.scrollTop / (scrollable.scrollHeight - scrollable.clientHeight);
            scroll.style.top = `${scrollRatio * (scrollable.clientHeight - thumbVerticalSize)}px`;
        },

        initRenderObserver() {
            const config = {
                root: this.$refs.optionsContainer,
                rootMargin: '30px',
                threshold: 0.5,
            };

            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach(({ target, isIntersecting }) => {
                    if (!isIntersecting) return;

                    const index = target.getAttribute('index');

                    if (index !== null) {
                        target.innerHTML = this.renderOption(this.currentOptions(index));
                        target.setAttribute('rendered', 'true');
                        observer.unobserve(target);
                    }
                });
            }, config);

            this.$refs.listing.querySelectorAll('li:not([rendered])').forEach((li) => {
                li.setAttribute('rendered', 'false');
                observer.observe(li);
            });
        },

        currentOptions(index = null) {
            const options = !this.asyncData.api ? this.displayOptions : this.guardOptions;

            return index ? options[index] : options;
        },

        initWatchers() {
            this.$watch('popover', (state) => {
                if (state) {
                    if (
                        this.asyncData.api &&
                        (this.asyncData?.alwaysFetch || this.options.length === 0)
                    ) {
                        this.fetchOptions();
                    }

                    this.$nextTick(() => {
                        if (window.innerWidth >= 1024) {
                            setTimeout(() => this.$refs.search?.focus(), 100);
                        }
                    });

                    this.$nextTick(() => this.initRenderObserver());
                }

                if (this.asyncData.api && this.asyncData.alwaysFetch && !state) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.options = [];
                            this.guardOptions = [];
                        }, 200);
                    });
                }

                this.$refs.input.dispatchEvent(new Event(state ? 'open' : 'close'));
            });

            this.$watch('search', (search) => {
                if (this.asyncData.api) {
                    this.canFetch = true;
                    this.offset = 0;
                    this.guardOptions = [];
                    this.asyncData.params['offset'] = 0;

                    this.fetchOptions();
                }

                this.$refs.optionsContainer?.scroll({ top: 0, left: 0, behavior: 'smooth' });

                if (this.asyncData.api) {
                    return this.fetchOptions();
                }

                this.displayOptions = this.searchOptions(search.toLocaleLowerCase());

                this.$nextTick(() => this.initRenderObserver());
            });

            this.$watch('options', (options) => {
                this.displayOptions = options;
            });
        },

        scrollAtBottom() {
            if (isIOS()) {
                const scrollable = document.getElementById(`content-${this.$el.id}`);

                this.isBottom =
                    scrollable.scrollHeight - scrollable.scrollTop === scrollable.clientHeight;

                return;
            }

            this.isBottom =
                Math.abs(
                    this.$refs.listing.scrollHeight -
                        this.$refs.listing.scrollTop -
                        this.$refs.listing.clientHeight,
                ) < 1;
        },

        handleShadow() {
            this.scrollAtBottom();

            document.getElementById(`content-${this.$el.id}`).addEventListener('scroll', () => {
                this.scrollAtBottom();
            });

            this.$refs.listing.addEventListener('scroll', () => {
                this.scrollAtBottom();
            });
        },

        initInfiniteScroll() {
            if (!this.asyncData.api) {
                return;
            }

            this.$refs.listing.addEventListener('touchmove', () => {
                this.handleScroll();
            });

            this.$refs.listing.addEventListener('scroll', () => {
                this.handleScroll();
            });
        },

        handleScroll() {
            const element = this.$refs.listing;

            let scrollHeight = element.scrollHeight;
            let currentScrollPosition = Math.round(element.scrollTop + element.clientHeight);

            if (
                scrollHeight * 0.8 <= currentScrollPosition &&
                this.asyncData.api &&
                !this.asyncData.fetching &&
                this.canFetch
            ) {
                if (!this.displayOptions.length) {
                    this.canFetch = false;

                    return;
                }

                this.offset += 20;

                this.asyncData.params['offset'] = this.offset;

                this.fetchOptions();

                this.guardOptions = [...new Set([...this.guardOptions, ...this.displayOptions])];
                this.canFetch = this.displayOptions.length === 20;
            }
        },

        initWireModel() {
            this.syncSelectedFromWireModel();

            if (this.asyncData.api) {
                this.$watch('asyncData.fetching', (isFetching) => {
                    if (!isFetching) {
                        this.handleScroll();
                    }
                });
            }

            if (this.hasWireModel && this.config.multiselect) {
                this.$watch('selectedOptions', (options, oldOptions) => {
                    if (this.mustSyncWireModel()) {
                        this.wireModel = options.map((option) => option.value);
                    }

                    if (JSON.stringify(options) !== JSON.stringify(oldOptions)) {
                        this.syncSelectedOptions();
                    }
                });

                this.$watch('wireModel', (options) => {
                    if (!Array.isArray(options)) {
                        throw new Error(
                            'The wire:model value must be an array to use the select as multiselect',
                        );
                    }

                    if (this.mustSyncWireModel()) {
                        this.asyncData.api
                            ? this.fetchSelected()
                            : this.syncSelectedFromWireModel();
                    }
                });

                if (this.wireModel?.length > 0 && this.asyncData.api) {
                    this.fetchSelected();
                }
            }

            if (this.hasWireModel && !this.config.multiselect) {
                this.$watch('selected', (option, oldOption) => {
                    this.wireModel = option?.value ?? null;

                    if (oldOption?.value !== option?.value) {
                        this.syncSelectedOptions();
                    }
                });

                this.$watch('wireModel', (value) => {
                    if (value === null || value === '') {
                        this.guardOptions
                            .filter((option) => option.isSelected)
                            .forEach((option) => (option.isSelected = false));

                        return (this.selected = undefined);
                    }

                    this.guardOptions
                        .filter((option) => option.isSelected)
                        .forEach((option) => (option.isSelected = false));

                    const selected = this.guardOptions.find((option) => option.value === value);
                    if (selected) selected.isSelected = true;

                    if (value && selected) {
                        this.selected = selected;
                    }
                });

                if (this.wireModel && this.asyncData.api) {
                    this.fetchSelected();
                }
            }
        },

        select(option) {
            if (this.config.readonly || option.disabled || option.readonly) return;

            if (!this.config.multiselect) {
                this.search = '';
            }

            if (this.config.multiselect) {
                const exists = this.selectedOptions.some(({ value }) => value === option.value);

                if (exists) return this.unSelect(option);

                this.$refs.input.dispatchEvent(new CustomEvent('selected', { detail: option }));

                option.isSelected = true;

                return this.selectedOptions.push(option);
            }

            if (!this.config.clearable && this.selected?.value === option.value) {
                return this.close();
            }

            this.selected = option.value === this.selected?.value ? undefined : option;

            this.$refs.input.dispatchEvent(new CustomEvent('selected', { detail: this.selected }));

            this.close();
        },

        clear() {
            if (!this.config.multiselect) {
                this.search = '';
            } else {
                this.selectedOptions.forEach((option) => (option.isSelected = false));
            }

            this.syncSelectedOptions();

            this.config.multiselect ? (this.selectedOptions = []) : (this.selected = undefined);

            this.$refs.input.dispatchEvent(new Event('clear'));
        },
    });
};
